import {
  FlipCameraIosRounded,
  CloseRounded,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Modal,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { environmentDetails } from "../../environment/environment";
import { editCompanyLogo, userService } from "../../services/userService";
import ApiAlert from "../AlertBox/ApiAlert";
import { useTheme } from "@mui/styles";

const { apiUrl } = environmentDetails;

const ProfileModal = ({ open, handleClose }) => {
  const [selectImg, setSelectImg] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  // alert states
  const [alert, setAlert] = useState(false);
  const [alertContent, setAlertContent] = useState(null);
  const [alertStatus, setAlertStatus] = useState(false);

  const { palette } = useTheme();
  const isSmall = useMediaQuery("(max-width:600px)");
  const isMedium = useMediaQuery("(max-width:900px)");

  const handleOnFileChange = (e) => {
    setSelectImg(e.target.files[0]);
  };

  const handleOnClose = () => {
    handleClose();
    setSelectImg(null);
  };

  useEffect(() => {
    let userD = JSON.parse(window.localStorage.getItem("userLogin"));
    if (userD) {
      setUserData(userD.data);
    }
  }, [open]);

  const updateUserProfilePhoto = () => {
    let formData = new FormData();
    formData.append("fullname", userData.fullname);
    formData.append("email", userData.email);
    formData.append("userid", userData.userid);
    formData.append("companyLogo", selectImg);
    setIsLoading(true);
    setIsError(false);
    editCompanyLogo(formData)
      .then((res) => {
        setAlert(true);
        setAlertContent("Update Successfully.");
        setAlertStatus(res?.data?.status);
        userService(res.data.data.companyLogo);
        handleClose();
        setIsLoading(false);
      })
      .catch((err) => {
        console.log("Error updating logo:", err);
        setIsError(true);
        setIsLoading(false);
      });
  };

  const handleAlertClose = () => {
    setAlert(false);
  };

  return (
    <>
      {alert && (
        <ApiAlert
          open={alert}
          snackbaronClose={handleAlertClose}
          alertonClose={handleAlertClose}
          alertType={alertStatus ? "success" : "error"}
          Content={alertContent}
        />
      )}

      <Modal
        open={open}
        onClose={handleOnClose}
        aria-labelledby="profile-modal-title"
        aria-describedby="profile-modal-description"
      >
        <Box
          sx={{
            ...style,
            width: isSmall ? "90%" : isMedium ? "60%" : "400px",
            p: isSmall ? 3 : 4,
          }}
        >
          {/* CLOSE BUTTON */}
          <Button
            onClick={handleOnClose}
            disabled={isLoading}
            sx={{
              position: "absolute",
              top: "10px",
              right: "10px",
              color: "primary.main",
              minWidth: "auto",
            }}
          >
            <CloseRounded />
          </Button>

          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "25px",
            }}
          >
            {/* PROFILE IMAGE CONTAINER */}
            <Box
              sx={{
                position: "relative",
                height: isSmall ? "110px" : "130px",
                width: isSmall ? "110px" : "130px",
              }}
            >
              {/* Circular border & image */}
              <Box
                sx={{
                  height: "100%",
                  width: "100%",
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: `2px solid ${palette.divider}`,
                  backgroundColor: "#f0f0f0",
                }}
              >
                <img
                  src={
                    selectImg
                      ? URL.createObjectURL(selectImg)
                      : userData?.companyLogo
                      ? userData?.companyLogo
                      : "https://www.ihna.edu.au/blog/wp-content/uploads/2022/10/user-dummy.png"
                  }
                  alt={userData?.fullname || "User"}
                  style={{
                    height: "100%",
                    width: "100%",
                    objectFit: "cover",
                    borderRadius: "50%",
                  }}
                  onError={(e) => {
                    e.target.src =
                      "https://www.ihna.edu.au/blog/wp-content/uploads/2022/10/user-dummy.png";
                  }}
                />
              </Box>

              {/* CAMERA ICON OVERLAY */}
              <label
                htmlFor="profilePhoto"
                style={{
                  position: "absolute",
                  bottom: "0px",
                  right: "-5px",
                  cursor: "pointer",
                  height: "40px",
                  width: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  backgroundColor: `${palette?.secondary?.main}`,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                  border: `2px solid ${palette.background.paper}`,
                }}
              >
                <FlipCameraIosRounded
                  sx={{
                    fontSize: "22px",
                    color: "primary.main",
                  }}
                />
              </label>
              <input
                type="file"
                accept="image/*"
                id="profilePhoto"
                style={{ display: "none" }}
                onChange={handleOnFileChange}
              />
            </Box>

            {/* USER DETAILS */}
            <Box
              sx={{
                backgroundColor: "#041C44",
                minWidth: isSmall ? "90%" : "300px",
                borderRadius: "12px",
                py: "12px",
                px: "10px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0px 4px 15px rgba(0,0,0,0.2)",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  color: "#C8CDD6",
                  fontSize: "16px",
                  fontWeight: 600,
                  textAlign: "center",
                  mb: "4px",
                }}
              >
                {userData?.fullname}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#C8CDD6",
                  fontSize: "14px",
                  textAlign: "center",
                  wordBreak: "break-all",
                }}
              >
                {userData?.email}
              </Typography>

              {/* ROLES */}
              <Box sx={{ width: "80%", mt: "10px" }}>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "14px",
                    color: "#C8CDD6",
                    mb: "6px",
                    fontWeight: 500,
                  }}
                >
                  Roles
                </Typography>
                <Box
                  sx={{
                    maxHeight: "150px",
                    overflowY: "auto",
                    "&::-webkit-scrollbar": {
                      width: "0.4em",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#FE9A25",
                      borderRadius: "5px",
                    },
                  }}
                >
                  {userData?.roles?.map((role, idx) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      sx={{
                        fontSize: "13px",
                        color: "#9397A0",
                        mb: "2px",
                      }}
                    >
                      {role}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* UPDATE BUTTON */}
            <Stack direction="row" spacing={2}>
              <Button
                disabled={!selectImg || isLoading}
                variant="contained"
                onClick={updateUserProfilePhoto}
                sx={{
                  backgroundColor: "secondary.main",
                  color: "primary.main",
                  fontWeight: 600,
                  borderRadius: "8px",
                  textTransform: "none",
                  px: "24px",
                  py: "6px",
                }}
              >
                {isLoading ? "Updating..." : "Update"}
              </Button>
            </Stack>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ProfileModal;

export const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  borderRadius: "15px",
  boxShadow: 24,
  border: "none",
  outline: "none",
};
